import { useAuth as useAuthKit } from "@workos-inc/authkit-react";
import { config } from "../config/env";
import { useGetCurrentUserAndOrganizationsQuery } from "../graphql/generated/types.gen";
import { endPersistedViewState } from "../utils/persistedViewState";

function signOutOfBrowser(): void {
	endPersistedViewState(localStorage);
	window.location.href = `${config.apiBaseUrl}/v1/auth/logout`;
}

// Fetches the WorkOS authorization URL and navigates to it directly, instead of
// navigating through GET /v1/auth. That hop is a pure redirector (arrive, 302 to
// WorkOS), which browser bounce-tracking mitigations flag — Brave intermittently
// synthesizes a 403 for it. Falls back to the redirect endpoint if the lookup
// fails or exceeds the timeout, so sign-in degrades to the old behavior rather
// than breaking or hanging.
function navigateToHostedAuth(params: URLSearchParams): void {
	const query = params.toString();
	const suffix = query ? `?${query}` : "";
	const fallbackUrl = `${config.apiBaseUrl}/v1/auth${suffix}`;
	// credentials deliberately omitted: the endpoint is anonymous by design and
	// its response must never vary by session.
	fetch(`${config.apiBaseUrl}/v1/auth/url${suffix}`, {
		credentials: "omit",
		signal: AbortSignal.timeout(4000),
	})
		.then((response) => {
			if (!response.ok) throw new Error(`auth URL lookup: ${response.status}`);
			return response.json() as Promise<{ url: string }>;
		})
		.then(({ url }) => {
			window.location.href =
				typeof url === "string" && url.startsWith("https://")
					? url
					: fallbackUrl;
		})
		.catch(() => {
			window.location.href = fallbackUrl;
		});
}

// Compatibility hook that provides consistent auth interface
// Uses backend session state instead of AuthKit state for authentication status
export function useAuthCompat() {
	// Always call useAuthKit to comply with hooks rules
	// When WorkOS is not configured, the main.tsx wraps with a no-op provider
	const authKitResult = useAuthKit();

	// Get actual authentication state from backend via GraphQL
	const { data: userData, loading } = useGetCurrentUserAndOrganizationsQuery();

	// Use AuthKit for login/logout actions if configured
	if (config.workosClientId) {
		return {
			// Use backend session state instead of AuthKit state
			user: userData?.currentUser || null,
			isLoading: loading,
			// Use AuthKit methods for login/logout
			signIn: (returnTo?: string) => {
				if (returnTo) {
					navigateToHostedAuth(new URLSearchParams({ returnTo }));
				} else {
					authKitResult.signIn();
				}
			},
			signOut: signOutOfBrowser,
			signUp: (returnTo?: string) => {
				// Always add screenHint=sign-up to show the sign-up screen
				const params = new URLSearchParams();
				if (returnTo) params.set("returnTo", returnTo);
				params.set("screenHint", "sign-up");
				navigateToHostedAuth(params);
			},
			getAccessToken: authKitResult.getAccessToken,
			organizationId: userData?.organizations?.[0]?.id || null,
			role: userData?.organizations?.[0]?.role || null,
			permissions: [],
			featureFlags: [],
			switchToOrganization: () => Promise.resolve(),
		};
	}

	// Fallback for when AuthKit is not configured
	return {
		user: userData?.currentUser || null,
		isLoading: loading,
		signIn: (returnTo?: string) => {
			const params = new URLSearchParams();
			if (returnTo) params.set("returnTo", returnTo);
			navigateToHostedAuth(params);
		},
		signOut: signOutOfBrowser,
		signUp: (returnTo?: string) => {
			// Always add screenHint=sign-up to show the sign-up screen
			const params = new URLSearchParams();
			if (returnTo) params.set("returnTo", returnTo);
			params.set("screenHint", "sign-up");
			navigateToHostedAuth(params);
		},
		getAccessToken: () => Promise.resolve(null),
		organizationId: userData?.organizations?.[0]?.id || null,
		role: userData?.organizations?.[0]?.role || null,
		permissions: [],
		featureFlags: [],
		switchToOrganization: () => Promise.resolve(),
	};
}
